import React, { useState, useEffect } from 'react';
import { TrendingUp, Users, Calendar, Eye } from 'lucide-react';
import Navbar from '../components/Navbar';
import { getMe, getAnalytics } from '../services/api';
import { toast } from 'sonner';

export default function DoctorDashboard() {
  const [user, setUser] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [userRes, analyticsRes] = await Promise.all([
        getMe(),
        getAnalytics()
      ]);
      setUser(userRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Navbar user={user} onLogout={() => setUser(null)} />
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F2A44]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar user={user} onLogout={() => setUser(null)} />

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="font-manrope font-bold text-3xl text-[#0F2A44] mb-2">
            Doctor Dashboard
          </h1>
          <p className="font-public text-[#4B5563]">Track your profile performance and patient engagement</p>
        </div>

        {/* Analytics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#4A90E2]/10 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-[#4A90E2]" />
              </div>
              <div>
                <p className="font-space font-medium text-2xl text-[#262626]">
                  {analytics?.searches || 0}
                </p>
                <p className="text-sm text-[#4B5563] font-public">Searches</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#2DD4BF]/10 rounded-lg flex items-center justify-center">
                <Eye className="h-6 w-6 text-[#2DD4BF]" />
              </div>
              <div>
                <p className="font-space font-medium text-2xl text-[#262626]">
                  {analytics?.profile_views || 0}
                </p>
                <p className="text-sm text-[#4B5563] font-public">Profile Views</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#0F2A44]/10 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-[#0F2A44]" />
              </div>
              <div>
                <p className="font-space font-medium text-2xl text-[#262626]">
                  {analytics?.contact_clicks || 0}
                </p>
                <p className="text-sm text-[#4B5563] font-public">Contact Clicks</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#EA580C]/10 rounded-lg flex items-center justify-center">
                <Calendar className="h-6 w-6 text-[#EA580C]" />
              </div>
              <div>
                <p className="font-space font-medium text-2xl text-[#262626]">
                  {analytics?.bookings || 0}
                </p>
                <p className="text-sm text-[#4B5563] font-public">Bookings</p>
              </div>
            </div>
          </div>
        </div>

        {/* Info Message */}
        <div className="bg-white rounded-xl border border-slate-100 p-8 text-center">
          <h2 className="font-manrope font-semibold text-xl text-[#0F2A44] mb-4">
            Real-Time Analytics
          </h2>
          <p className="font-public text-[#4B5563] leading-relaxed max-w-2xl mx-auto">
            Your analytics are updated in real-time as patients interact with your profile. 
            Track searches, profile views, contact clicks, and booking requests to understand your reach and engagement.
          </p>
        </div>
      </div>
    </div>
  );
}